import i18n from "../i18n/index.js";

const BASE =
  (window as unknown as { __config__?: { API_URL?: string } }).__config__
    ?.API_URL ?? "/api/v1";

/** Base URL of the API, for the few raw requests that cannot go through apiFetch. */
export const API_BASE = BASE;

const RETRY_DELAYS_MS = [500, 1500];

/**
 * Thrown for a real HTTP error response (server was reached). `message` is
 * either a specific, user-facing message the API sent back (e.g. a
 * validation error), or a generic translated fallback for anything else —
 * never the raw `{"statusCode":500,...}` body, which is meaningless to a
 * non-technical or non-Norwegian/English-speaking fisher.
 */
export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

/** Pulls a user-facing message out of an API error body, if there is one. */
function parseErrorMessage(body: string): string | null {
  try {
    const parsed = JSON.parse(body);
    return (
      (typeof parsed.error === "string" && parsed.error) ||
      (typeof parsed.message === "string" && parsed.message) ||
      null
    );
  } catch {
    return null;
  }
}

/**
 * Thrown when a request never reached the server (connection dropped,
 * DNS failure, flaky wifi) even after retrying — as opposed to a real
 * HTTP error response, which means the server was reached. Callers can
 * use this to decide whether it's safe to fall back to offline queueing.
 */
export class NetworkError extends Error {
  originalError: unknown;
  constructor(originalError: unknown) {
    super("Network request failed");
    this.originalError = originalError;
  }
}

/**
 * Like fetch(), but retries with backoff when the request never reaches
 * the server at all (e.g. a dropped connection). A response that comes
 * back — even an error status — means the server was reached, so it is
 * never retried here: retrying a POST after a real response risks
 * double-submitting whatever the request just did.
 */
async function fetchWithRetry(
  input: string,
  init: RequestInit,
): Promise<Response> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fetch(input, init);
    } catch (err) {
      if (attempt >= RETRY_DELAYS_MS.length) throw new NetworkError(err);
      await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]));
    }
  }
}

export async function apiFetch<T>(
  path: string,
  options?: RequestInit & { token?: string; getToken?: () => Promise<string> },
): Promise<T> {
  const { token: staticToken, getToken, ...rest } = options ?? {};
  const token = getToken ? await getToken() : staticToken;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(rest.headers as Record<string, string> | undefined),
  };

  const res = await fetchWithRetry(`${BASE}${path}`, { ...rest, headers });

  if (!res.ok) {
    const body = await res.text();
    // A 5xx is a server-side failure — its body is a crash detail, not
    // something to show a fisher. A 4xx from our own routes usually carries
    // a specific, already-useful message (e.g. "Velg et vald"), so keep it.
    const message =
      res.status < 500 ? (parseErrorMessage(body) ?? undefined) : undefined;
    throw new ApiError(res.status, message ?? i18n.t("common.error"));
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

/** Exposed so other raw fetch() call sites (e.g. the presigned photo upload PUT) get the same retry behavior. */
export { fetchWithRetry };

/**
 * Presigns and uploads a catch photo. Shared by the immediate upload-on-select
 * path and the offline-queue drain path (retrying a photo that was captured
 * while offline), so both go through the exact same request/retry behavior.
 */
export async function uploadPhoto(
  blob: Blob,
  mimeType: string,
  getToken: () => Promise<string>,
): Promise<string> {
  const { uploadUrl, imageUrl, contentType } = await apiFetch<{
    uploadUrl: string;
    imageUrl: string;
    contentType: string;
  }>("/uploads/presign", {
    method: "POST",
    body: JSON.stringify({ contentType: mimeType }),
    getToken,
  });
  const res = await fetchWithRetry(uploadUrl, {
    method: "PUT",
    body: blob,
    headers: { "Content-Type": contentType },
  });
  if (!res.ok) throw new Error(`Upload failed (${res.status})`);
  return imageUrl;
}
